import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Select,
  Switch,
  Textarea,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowSync20Regular,
  Delete20Regular,
  Dismiss24Regular,
  Edit20Regular,
  Search20Regular,
} from "@fluentui/react-icons";
import { CRM_BASE_URL, CRM_LANGUAGE } from "../../types/Crmconfig";
import styles from "./Settup.module.css";

/* ------------------------------------------------------------------ */
/* CRM setup screens.                                                  */
/* One screen, driven by the CRM service catalog (crmCatalog.ts): each */
/* setup list below is the same endpoint the CRM screens already use.  */
/* Roles and permissions are NOT here: they live in Roles &            */
/* Permissions (central auth).                                         */
/* ------------------------------------------------------------------ */

type FieldType = "string" | "guid" | "boolean" | "integer" | "number" | "date" | "datetime" | "json";

const CATALOG: Record<string, { l: string; p: string; path: string; f: [string, FieldType, 0 | 1][] }> = {"admin.business-units":{"l":"Business unit","p":"Business units","path":"api/admin/business-units","f":[["organizationId","guid",1],["parentBusinessUnitId","guid",0],["name","string",1],["divisionName","string",0],["isDisabled","boolean",0]]},"admin.crm-teams":{"l":"Team","p":"Teams","path":"api/admin/crm-teams","f":[["businessUnitId","guid",1],["name","string",1],["teamType","integer",0],["administratorId","guid",0]]},"admin.team":{"l":"Sales team member","p":"Sales team","path":"api/admin/team","f":[["businessUnitId","guid",0],["jobTitle","string",0],["accessMode","integer",0],["sipPhoneExtension","string",0]]},"admin.team-memberships":{"l":"Team membership","p":"Team memberships","path":"api/admin/team-memberships","f":[["teamId","guid",1],["userId","guid",1],["addedOn","datetime",0]]},"admin.territories":{"l":"Territory","p":"Territories","path":"api/admin/territories","f":[["code","string",1],["name","string",1],["parentId","guid",0],["managerId","guid",0],["countryCodes","string",0],["states","string",0],["cities","string",0],["postalPrefixes","string",0],["priority","integer",0],["description","string",0],["isActive","boolean",0]]},"admin.pipelines":{"l":"Pipeline","p":"Pipelines","path":"api/admin/pipelines","f":[["name","string",1],["appliesTo","string",0],["isDefault","boolean",0],["isActive","boolean",0],["description","string",0]]},"admin.picklists":{"l":"Picklist value","p":"Picklist values","path":"api/admin/picklists","f":[["listKey","string",1],["value","string",1],["label","string",0],["order","integer",0],["isActive","boolean",0],["color","string",0]]},"admin.custom-fields":{"l":"Custom field definition","p":"Custom field definitions","path":"api/admin/custom-fields","f":[["entityName","string",1],["key","string",1],["label","string",1],["fieldType","string",0],["options","json",0],["required","boolean",0],["order","integer",0],["section","string",0],["helpText","string",0],["isActive","boolean",0]]},"admin.scoring-rules":{"l":"Scoring rule","p":"Scoring rules","path":"api/admin/scoring-rules","f":[["name","string",1],["field","string",1],["operator","string",0],["value","string",1],["points","integer",0],["order","integer",0],["isActive","boolean",0]]},"sales.goal-metrics":{"l":"Goal metric","p":"Goal metrics","path":"api/sales/goal-metrics","f":[["name","string",1],["isAmount","boolean",0],["sourceEntity","string",0],["sourceAttribute","string",0]]},"admin.goals":{"l":"Sales goal","p":"Sales goals","path":"api/admin/goals","f":[["goalMetricId","guid",0],["parentGoalId","guid",0],["fiscalPeriod","string",0],["fiscalYear","integer",0],["targetInteger","integer",0],["actualMoney","number",0],["actualInteger","integer",0],["inProgressMoney","number",0],["percentageAchieved","number",0]]},"sales.competitors":{"l":"Competitor","p":"Competitors","path":"api/sales/competitors","f":[["opportunities","string",0],["threats","string",0]]},"workflow.assignment-rules":{"l":"Assignment rule","p":"Assignment rules","path":"api/workflow/assignment-rules","f":[["name","string",1],["entity","string",1],["isActive","boolean",0],["strategy","string",0],["conditionMode","string",0],["conditionsJson","json",0],["poolJson","json",0],["mapField","string",0],["mapJson","json",0],["fallbackOwnerId","guid",0],["fallbackOwnerName","string",0],["openFilter","string",0],["autoApply","boolean",0],["respectChosenOwner","boolean",0],["priority","integer",0]]},"workflow.approval-policies":{"l":"Approval policy","p":"Approval policies","path":"api/workflow/approval-policies","f":[["name","string",1],["description","string",0],["entity","string",1],["isActive","boolean",0],["conditionMode","string",0],["conditionsJson","json",0],["stepsJson","json",0],["onApproveJson","json",0],["onRejectJson","json",0],["expiryHours","integer",0],["allowSelfApproval","boolean",0],["amountField","string",0],["priority","integer",0]]},"commerce.product-families":{"l":"Product family","p":"Product families","path":"api/commerce/product-families","f":[["name","string",1],["parentFamilyId","guid",0]]},"commerce.unit-groups":{"l":"Unit group","p":"Unit groups","path":"api/commerce/unit-groups","f":[["name","string",1]]},"commerce.unit-of-measures":{"l":"Unit of measure","p":"Units of measure","path":"api/commerce/unit-of-measures","f":[["unitGroupId","guid",1],["name","string",1],["baseUnitId","guid",0],["quantity","number",0]]},"commerce.products":{"l":"Product","p":"Products","path":"api/commerce/products","f":[["productFamilyId","guid",0],["productType","integer",0],["defaultUnitGroupId","guid",0],["defaultUnitId","guid",0],["standardCost","number",0],["currentCost","number",0],["quantityOnHand","number",0],["quantityDecimal","integer",0],["vendorName","string",0]]},"commerce.price-lists":{"l":"Price list","p":"Price lists","path":"api/commerce/price-lists","f":[["currencyId","guid",0]]},"commerce.discount-lists":{"l":"Discount list","p":"Discount lists","path":"api/commerce/discount-lists","f":[["name","string",1],["isAmountType","boolean",0],["currencyId","guid",0]]},"commerce.discounts":{"l":"Discount","p":"Discounts","path":"api/commerce/discounts","f":[["discountListId","guid",1],["lowQuantity","number",0],["highQuantity","number",0],["percentage","number",0],["amount","number",0]]},"commerce.payment-terms":{"l":"Payment term","p":"Payment terms","path":"api/commerce/payment-terms","f":[["code","string",1],["name","string",1],["dueDays","integer",0],["earlyDiscountPct","number",0],["earlyDiscountDays","integer",0],["isDefault","boolean",0],["isActive","boolean",0]]},"globalization.rates":{"l":"Exchange rate","p":"Exchange rates","path":"api/globalization/rates","f":[["fromCurrency","string",1],["toCurrency","string",1],["rate","number",1],["rateType","string",0],["effectiveDate","date",0],["note","string",0]]},"globalization.tax-codes":{"l":"Tax code","p":"Tax codes","path":"api/globalization/tax-codes","f":[["code","string",1],["name","string",1],["rate","number",1],["type","string",0],["countryCode","string",0],["isCompound","boolean",0],["isInclusive","boolean",0],["isActive","boolean",0],["effectiveFrom","date",0],["effectiveTo","date",0],["reportingCategory","string",0],["description","string",0]]},"service.service-queues":{"l":"Service queue","p":"Service queues","path":"api/service/service-queues","f":[["name","string",1],["queueType","integer",0],["email","string",0],["ownerId","guid",0]]},"service.sla-policies":{"l":"SLA policy","p":"SLA policies","path":"api/service/sla-policies","f":[["isDefault","boolean",0],["allowPauseResume","boolean",0],["businessHoursJson","string",0]]},"admin.subject-categories":{"l":"Subject category","p":"Subject categories","path":"api/admin/subject-categories","f":[["parentSubjectId","guid",0],["title","string",1],["description","string",0]]},"telephony.call-dispositions":{"l":"Call disposition","p":"Call dispositions","path":"api/telephony/call-dispositions","f":[["code","string",1],["label","string",1],["isPositiveOutcome","boolean",0]]},"activities.email-templates":{"l":"Email template","p":"Email templates","path":"api/activities/email-templates","f":[["languageCode","integer",0],["isPersonal","boolean",0],["ownerId","guid",0]]},"partners.tiers":{"l":"Partner tier","p":"Partner tiers","path":"api/partners/tiers","f":[["name","string",1],["rank","integer",1],["minRevenue","number",0],["minCertifiedUsers","integer",0],["minScore","number",0],["discountPct","number",0],["coopAccrualPct","number",0],["benefits","string",0],["active","boolean",0]]},"success.programs":{"l":"Loyalty program","p":"Loyalty programs","path":"api/success/programs","f":[["name","string",1],["description","string",0],["pointsPerUnit","number",0],["pointValue","number",0],["expiryMonths","integer",0],["minRedeemPoints","integer",0],["autoEnroll","boolean",0],["effectiveFrom","date",0]]},"success.loyalty-tiers":{"l":"Loyalty tier","p":"Loyalty tiers","path":"api/success/loyalty-tiers","f":[["programId","guid",1],["name","string",1],["minPoints","integer",1],["multiplier","number",0],["benefits","string",0]]},"success.rewards":{"l":"Reward","p":"Rewards","path":"api/success/rewards","f":[["programId","guid",1],["name","string",1],["description","string",0],["pointsCost","integer",1],["stock","integer",0],["active","boolean",0]]},"success.playbooks":{"l":"Playbook","p":"Playbooks","path":"api/success/playbooks","f":[["name","string",1],["description","string",0],["trigger","string",0],["segment","string",0],["active","boolean",0]]},"agreements.templates":{"l":"Document template","p":"Document templates","path":"api/agreements/templates","f":[["name","string",1],["kind","string",0],["description","string",0],["body","string",1],["active","boolean",0]]},"agreements.blocks":{"l":"Content block","p":"Content blocks","path":"api/agreements/blocks","f":[["code","string",0],["title","string",1],["category","string",0],["body","string",1],["active","boolean",0]]},"revenue.meters":{"l":"Usage meter","p":"Usage meters","path":"api/revenue/meters","f":[["code","string",1],["name","string",1],["unit","string",0],["aggregation","string",0],["description","string",0],["active","boolean",0]]},"incentives.plans":{"l":"Incentive plan","p":"Incentive plans","path":"api/incentives/plans","f":[["name","string",1],["description","string",0],["model","string",0],["periodType","string",0],["effectiveFrom","date",0],["effectiveTo","date",0],["thresholdPct","number",0],["targetIncentive","number",0],["capAmount","number",0],["maxPayoutPct","number",0]]},"incentives.quotas":{"l":"Quota","p":"Quotas","path":"api/incentives/quotas","f":[["userId","guid",1],["userName","string",0],["periodType","string",0],["periodStart","date",1],["amount","number",1],["notes","string",0]]},"incentives.credit-rules":{"l":"Credit rule","p":"Credit rules","path":"api/incentives/credit-rules","f":[["name","string",1],["description","string",0],["kind","string",0],["priority","integer",0],["active","boolean",0],["types","string",0],["minAmount","number",0],["maxAmount","number",0]]},"fieldservice.skills":{"l":"Skill","p":"Skills","path":"api/fieldservice/skills","f":[["name","string",1],["description","string",0]]},"fieldservice.service-types":{"l":"Service type","p":"Service types","path":"api/fieldservice/service-types","f":[["name","string",1],["description","string",0],["defaultMinutes","integer",0],["billable","boolean",0],["hourlyRate","number",0],["fixedPrice","number",0],["minBillableMinutes","integer",0],["billingIncrementMinutes","integer",0],["billTravel","boolean",0],["laborProductId","guid",0],["requiresSignature","boolean",0],["active","boolean",0]]},"fieldservice.warehouses":{"l":"Warehouse","p":"Warehouses","path":"api/fieldservice/warehouses","f":[["name","string",1],["kind","string",1],["technicianId","guid",0],["active","boolean",0]]},"fieldservice.parts":{"l":"Part","p":"Parts","path":"api/fieldservice/parts","f":[["sku","string",1],["name","string",1],["productId","guid",0],["unit","string",0],["unitCost","number",0],["unitPrice","number",0],["active","boolean",0]]}};

/** Sidebar structure for Settup.tsx. */
export const CRM_SETUP_GROUPS: { title: string; items: { key: string; title: string }[] }[] = [
 {
  "title": "Organization",
  "items": [
   {
    "key": "admin.business-units",
    "title": "Business units"
   },
   {
    "key": "admin.crm-teams",
    "title": "Teams"
   },
   {
    "key": "admin.team",
    "title": "Sales team"
   },
   {
    "key": "admin.team-memberships",
    "title": "Team memberships"
   },
   {
    "key": "admin.territories",
    "title": "Territories"
   }
  ]
 },
 {
  "title": "Sales setup",
  "items": [
   {
    "key": "admin.pipelines",
    "title": "Pipelines"
   },
   {
    "key": "admin.picklists",
    "title": "Picklist values"
   },
   {
    "key": "admin.custom-fields",
    "title": "Custom field definitions"
   },
   {
    "key": "admin.scoring-rules",
    "title": "Scoring rules"
   },
   {
    "key": "sales.goal-metrics",
    "title": "Goal metrics"
   },
   {
    "key": "admin.goals",
    "title": "Sales goals"
   },
   {
    "key": "sales.competitors",
    "title": "Competitors"
   },
   {
    "key": "workflow.assignment-rules",
    "title": "Assignment rules"
   },
   {
    "key": "workflow.approval-policies",
    "title": "Approval policies"
   }
  ]
 },
 {
  "title": "Products & pricing",
  "items": [
   {
    "key": "commerce.product-families",
    "title": "Product families"
   },
   {
    "key": "commerce.unit-groups",
    "title": "Unit groups"
   },
   {
    "key": "commerce.unit-of-measures",
    "title": "Units of measure"
   },
   {
    "key": "commerce.products",
    "title": "Products"
   },
   {
    "key": "commerce.price-lists",
    "title": "Price lists"
   },
   {
    "key": "commerce.discount-lists",
    "title": "Discount lists"
   },
   {
    "key": "commerce.discounts",
    "title": "Discounts"
   },
   {
    "key": "commerce.payment-terms",
    "title": "Payment terms"
   },
   {
    "key": "globalization.rates",
    "title": "Exchange rates"
   },
   {
    "key": "globalization.tax-codes",
    "title": "Tax codes"
   }
  ]
 },
 {
  "title": "Service & support",
  "items": [
   {
    "key": "service.service-queues",
    "title": "Service queues"
   },
   {
    "key": "service.sla-policies",
    "title": "SLA policies"
   },
   {
    "key": "admin.subject-categories",
    "title": "Subject categories"
   },
   {
    "key": "telephony.call-dispositions",
    "title": "Call dispositions"
   },
   {
    "key": "activities.email-templates",
    "title": "Email templates"
   }
  ]
 },
 {
  "title": "Engagement",
  "items": [
   {
    "key": "partners.tiers",
    "title": "Partner tiers"
   },
   {
    "key": "success.programs",
    "title": "Loyalty programs"
   },
   {
    "key": "success.loyalty-tiers",
    "title": "Loyalty tiers"
   },
   {
    "key": "success.rewards",
    "title": "Rewards"
   },
   {
    "key": "success.playbooks",
    "title": "Playbooks"
   },
   {
    "key": "agreements.templates",
    "title": "Document templates"
   },
   {
    "key": "agreements.blocks",
    "title": "Content blocks"
   }
  ]
 },
 {
  "title": "Revenue & field service",
  "items": [
   {
    "key": "revenue.meters",
    "title": "Usage meters"
   },
   {
    "key": "incentives.plans",
    "title": "Incentive plans"
   },
   {
    "key": "incentives.quotas",
    "title": "Quotas"
   },
   {
    "key": "incentives.credit-rules",
    "title": "Credit rules"
   },
   {
    "key": "fieldservice.skills",
    "title": "Skills"
   },
   {
    "key": "fieldservice.service-types",
    "title": "Service types"
   },
   {
    "key": "fieldservice.warehouses",
    "title": "Warehouses"
   },
   {
    "key": "fieldservice.parts",
    "title": "Parts"
   }
  ]
 }
];


interface SettupCrmProps {
  /** a catalog key such as "admin.pipelines" (see CRM_SETUP_GROUPS) */
  entityKey: string;
  token: string;
  baseUrl?: string;
}

type Intent = "success" | "error" | "warning";
type Rec = { id: string; version?: string; [k: string]: any };
interface Fld {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  long?: boolean;
}

/* ---------- http (same contract as Api/crmClient.ts: gateway + bearer + If-Match) ---------- */

class CrmError extends Error {}

const request = async (
  baseUrl: string,
  token: string,
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  extra: Record<string, string> = {}
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token ?? ""}`,
    ...extra,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (CRM_LANGUAGE) headers["Accept-Language"] = CRM_LANGUAGE as string;
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new CrmError("Can't reach the CRM service. Check your connection.");
  }
  if (res.status === 204) return undefined;
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    let msg = data?.detail || data?.message || data?.title || "";
    if (data?.errors && typeof data.errors === "object") {
      const first = Object.values(data.errors as Record<string, string[]>)[0];
      if (Array.isArray(first) && first[0]) msg = first[0];
    }
    if (!msg) {
      msg =
        res.status === 401
          ? "The CRM service did not accept your sign-in. Sign in again."
          : res.status === 403
          ? "You don't have permission to do that."
          : res.status === 409 || res.status === 412
          ? "Someone else changed this record. It was reloaded; try again."
          : `Request failed (${res.status}).`;
    }
    throw new CrmError(String(msg));
  }
  return data;
};

/* ---------- field model ---------- */

const human = (k: string) =>
  k
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\bId\b/g, "")
    .replace(/\bJson\b/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const LONG = /(body|description|benefits|notes|helpText|strengths|weaknesses|threats|opportunities|businessHours)/i;

/** Fields that point at another record: field name -> catalog screen it picks from. */
const LOOKUP: Record<string, { key: string; by?: string }> = {
  businessUnitId: { key: "admin.business-units" },
  parentBusinessUnitId: { key: "admin.business-units" },
  teamId: { key: "admin.crm-teams" },
  parentId: { key: "self" },
  parentSubjectId: { key: "admin.subject-categories" },
  goalMetricId: { key: "sales.goal-metrics" },
  parentGoalId: { key: "admin.goals" },
  productFamilyId: { key: "commerce.product-families" },
  parentFamilyId: { key: "commerce.product-families" },
  unitGroupId: { key: "commerce.unit-groups" },
  defaultUnitGroupId: { key: "commerce.unit-groups" },
  defaultUnitId: { key: "commerce.unit-of-measures" },
  baseUnitId: { key: "commerce.unit-of-measures" },
  discountListId: { key: "commerce.discount-lists" },
  programId: { key: "success.programs" },
  productId: { key: "commerce.products" },
  laborProductId: { key: "commerce.products" },
  technicianId: { key: "fieldservice.technicians" },
  userId: { key: "admin.team", by: "userId" },
  ownerId: { key: "admin.team", by: "userId" },
  managerId: { key: "admin.team", by: "userId" },
  administratorId: { key: "admin.team", by: "userId" },
  fallbackOwnerId: { key: "admin.team", by: "userId" },
};
const LOOKUP_PATH: Record<string, string> = {
  "fieldservice.technicians": "api/fieldservice/technicians",
};
const pathOf = (key: string) => LOOKUP_PATH[key] ?? CATALOG[key]?.path ?? "";

const RESERVED = /^(id|number|version|rowVersion|companyId|tenantId|createdBy|createdAt|createdOn|updatedBy|updatedAt|updatedOn|modifiedAt|modifiedOn|modifiedBy|ownerName|byId)$/i;

const normType = (t: unknown): FieldType => {
  const x = String(t ?? "").toLowerCase();
  if (x.includes("guid") || x === "uuid") return "guid";
  if (x.startsWith("bool")) return "boolean";
  if (x.startsWith("int") || x === "long") return "integer";
  if (x === "number" || x === "decimal" || x === "double" || x === "float") return "number";
  if (x === "datetime" || x === "date-time") return "datetime";
  if (x === "date") return "date";
  if (x === "json" || x === "object" || x === "array") return "json";
  return "string";
};

const toFld = (name: string, type: FieldType, required: boolean, label?: string): Fld => ({
  name,
  label: label || human(name) || name,
  type,
  required,
  long: type === "json" || (type === "string" && LONG.test(name)),
});

/** The service may publish the real form at GET <path>/schema; accept the shapes it is likely to use. */
const readSchema = (data: any): Fld[] | null => {
  const list: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.fields)
    ? data.fields
    : Array.isArray(data?.input)
    ? data.input
    : Array.isArray(data?.properties)
    ? data.properties
    : [];
  const out: Fld[] = [];
  for (const f of list) {
    const name = typeof f === "string" ? f : f?.name ?? f?.key ?? f?.field;
    if (typeof name !== "string" || !name || RESERVED.test(name)) continue;
    out.push(toFld(name, normType(typeof f === "string" ? "string" : f.type), !!(f?.required ?? false), typeof f?.label === "string" ? f.label : undefined));
  }
  return out.length ? out : null;
};

/** Base fields (name, description...) are not listed in the catalog input, so pick them up from real records. */
const sampleFields = (rows: Rec[], known: Set<string>): Fld[] => {
  const seen = new Map<string, any>();
  rows.slice(0, 20).forEach((r) =>
    Object.entries(r).forEach(([k, v]) => {
      if (RESERVED.test(k) || known.has(k) || seen.has(k)) return;
      if (v !== null && v !== undefined && typeof v !== "object") seen.set(k, v);
    })
  );
  const out: Fld[] = [];
  seen.forEach((v, k) => {
    if (/(At|On|Date|Name)$/.test(k) && k !== "name" && !/^(effective|fiscal)/.test(k)) return; // system stamps and denormalised names
    if (typeof v === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v)) return; // ids we have no input for
    if (/^(status|state|isDeleted|isLocked)$/i.test(k)) return;
    const t: FieldType = typeof v === "boolean" ? "boolean" : typeof v === "number" ? (Number.isInteger(v) ? "integer" : "number") : "string";
    out.push(toFld(k, t, k === "name" || k === "title"));
  });
  return out;
};

/* ---------- display ---------- */

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ACTIVE_KEYS = /^(isActive|active)$/i;
const OFF_KEYS = /^(isDisabled)$/i;
const FIRST = /^(code|name|title|label|key|listKey|value|sku|fromCurrency)$/i;

interface ColDef {
  key: string;
  label: string;
}

const pickColumns = (rows: Rec[], fields: Fld[]): ColDef[] => {
  const keys: string[] = [];
  rows.slice(0, 30).forEach((r) => Object.keys(r).forEach((k) => !keys.includes(k) && keys.push(k)));
  const usable = keys.filter((k) => {
    if (RESERVED.test(k) && k !== "number") return false;
    if (/(At|On)$/.test(k) && !/^(effective)/.test(k)) return false;
    const sample = rows.find((r) => r[k] !== null && r[k] !== undefined && r[k] !== "")?.[k];
    if (sample === undefined) return false;
    if (typeof sample === "object") return false;
    if (typeof sample === "string" && GUID.test(sample)) return false;
    return true;
  });
  const first: string[] = usable.filter((k) => k === "number");
  const lead = usable.filter((k) => FIRST.test(k));
  const rest = usable.filter((k) => !first.includes(k) && !lead.includes(k));
  const ordered = [...first, ...lead, ...rest].slice(0, 7);
  const labels = new Map(fields.map((f) => [f.name, f.label]));
  return ordered.map((k) => ({ key: k, label: k === "number" ? "No." : labels.get(k) ?? human(k) }));
};

const showCell = (key: string, v: any): React.ReactNode => {
  if (v === null || v === undefined || v === "") return "";
  if (typeof v === "boolean") {
    const on = OFF_KEYS.test(key) ? !v : v;
    const word = ACTIVE_KEYS.test(key) ? (v ? "Active" : "Inactive") : OFF_KEYS.test(key) ? (v ? "Disabled" : "Enabled") : v ? "Yes" : "No";
    return <span className={`${styles.accPill} ${on ? styles.accTone_green : styles.accTone_grey}`}>{word}</span>;
  }
  if (key === "status" && typeof v === "string")
    return <span className={`${styles.accPill} ${/active|open|won|approved|enabled/i.test(v) ? styles.accTone_green : styles.accTone_grey}`}>{v}</span>;
  if (typeof v === "number") return v.toLocaleString("en-US", { maximumFractionDigits: 4 });
  const str = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(str) && /(date|from|to|start|end)/i.test(key)) return str.slice(0, 10);
  return str.length > 80 ? `${str.slice(0, 80)}…` : str;
};

const labelOf = (r: Rec): string => String(r.name ?? r.title ?? r.label ?? r.code ?? r.number ?? r.id);
const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Something went wrong");

/* ---------- form ---------- */

const initialValue = (f: Fld, rec: Rec | null): string | boolean => {
  const raw = rec ? rec[f.name] : undefined;
  if (f.type === "boolean") return raw === undefined || raw === null ? (!rec && /^(isActive|active)$/i.test(f.name)) : !!raw;
  if (raw === undefined || raw === null) return "";
  if (f.type === "json") return typeof raw === "string" ? raw : JSON.stringify(raw, null, 2);
  if (f.type === "date" || f.type === "datetime") return String(raw).slice(0, f.type === "date" ? 10 : 16);
  return String(raw);
};

const castOut = (f: Fld, raw: string | boolean): { ok: true; value: unknown } | { ok: false; error: string } => {
  if (f.type === "boolean") return { ok: true, value: !!raw };
  const t = String(raw ?? "").trim();
  if (t === "") return { ok: true, value: null };
  switch (f.type) {
    case "integer":
    case "number": {
      const x = Number(t);
      return Number.isFinite(x) ? { ok: true, value: f.type === "integer" ? Math.trunc(x) : x } : { ok: false, error: `${f.label} must be a number.` };
    }
    case "json":
      try {
        return { ok: true, value: JSON.parse(t) };
      } catch {
        return { ok: false, error: `${f.label} must be valid JSON.` };
      }
    case "datetime":
      return { ok: true, value: new Date(t).toISOString() };
    default:
      return { ok: true, value: t };
  }
};

const RecordDialog: React.FC<{
  title: string;
  submit: string;
  fields: Fld[];
  rec: Rec | null;
  options: Record<string, { v: string; l: string }[]>;
  onClose: () => void;
  onSave: (values: Record<string, unknown>) => Promise<string | null>;
}> = ({ title, submit, fields, rec, options, onClose, onSave }) => {
  const [v, setV] = useState<Record<string, string | boolean>>(() => Object.fromEntries(fields.map((f) => [f.name, initialValue(f, rec)])));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    if (busy) return;
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      if (f.required && f.type !== "boolean" && !String(v[f.name] ?? "").trim()) return setErr(`${f.label} is required.`);
      const c = castOut(f, v[f.name]);
      if (!c.ok) return setErr(c.error);
      const before = initialValue(f, rec);
      if (rec) {
        if (before === v[f.name]) continue; // edits send only what changed
      } else if (f.type === "boolean" ? before === v[f.name] : c.value === null) {
        continue; // blank on create: leave it to the service default
      }
      out[f.name] = c.value;
    }
    setBusy(true);
    setErr(null);
    const bad = await onSave(out);
    setBusy(false);
    if (bad) setErr(bad);
  };

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()}>
      <DialogSurface className={styles.accDialog}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            {title}
          </DialogTitle>
          <DialogContent>
            <div className={styles.accForm}>
              {fields.map((f) => {
                const opts = options[f.name];
                const wide = f.long || f.type === "json" || (f.type === "string" && f.name.length > 0 && /(name|title)$/i.test(f.name));
                return (
                  <div key={f.name} className={wide ? styles.accFormFull : undefined}>
                    {f.type === "boolean" ? (
                      <Switch
                        label={f.label}
                        checked={!!v[f.name]}
                        onChange={(_, d) => setV({ ...v, [f.name]: d.checked })}
                      />
                    ) : (
                      <Field
                        label={f.label}
                        required={f.required}
                        hint={f.type === "json" ? "JSON" : f.type === "guid" && !opts ? "Record id" : undefined}
                      >
                        {opts ? (
                          <Select value={String(v[f.name] ?? "")} onChange={(_, d) => setV({ ...v, [f.name]: d.value })}>
                            <option value="">{f.required ? "Select…" : "None"}</option>
                            {opts.map((o) => (
                              <option key={o.v} value={o.v}>
                                {o.l}
                              </option>
                            ))}
                          </Select>
                        ) : f.long ? (
                          <Textarea
                            className={f.type === "json" ? styles.accMonoArea : undefined}
                            rows={f.type === "json" ? 5 : 3}
                            resize="vertical"
                            value={String(v[f.name] ?? "")}
                            onChange={(_, d) => setV({ ...v, [f.name]: d.value })}
                          />
                        ) : (
                          <Input
                            type={f.type === "integer" || f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : "text"}
                            value={String(v[f.name] ?? "")}
                            onChange={(_, d) => setV({ ...v, [f.name]: d.value })}
                          />
                        )}
                      </Field>
                    )}
                  </div>
                );
              })}
            </div>
            {err && (
              <div className={styles.accDlgErr} role="alert">
                {err}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger action="close">
              <Button>Cancel</Button>
            </DialogTrigger>
            <Button appearance="primary" disabled={busy} onClick={go}>
              {busy ? "Working…" : submit}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

/* ---------- the page ---------- */

const PAGE_SIZE = 200;
const MAX_ROWS = 2000;

const SettupCrm: React.FC<SettupCrmProps> = ({ entityKey, token, baseUrl = CRM_BASE_URL as string }) => {
  const def = CATALOG[entityKey];
  const toasterId = useId("crm-setup-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notify = useCallback(
    (message: string, intent: Intent = "success") =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, position: "top-end", timeout: 4000 }
      ),
    [dispatchToast]
  );

  const [rows, setRows] = useState<Rec[]>([]);
  const [total, setTotal] = useState(0);
  const [schema, setSchema] = useState<Fld[] | null>(null);
  const [options, setOptions] = useState<Record<string, { v: string; l: string }[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [form, setForm] = useState<{ rec: Rec | null } | null>(null);
  const [del, setDel] = useState<Rec | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!def) return;
    setLoading(true);
    setLoadErr(null);
    try {
      const all: Rec[] = [];
      let count = 0;
      for (let page = 1; all.length < MAX_ROWS; page++) {
        const data = await request(baseUrl, token, "GET", `${def.path}?page=${page}&pageSize=${PAGE_SIZE}`);
        const items: Rec[] = Array.isArray(data) ? data : data?.items ?? [];
        count = Number(data?.total ?? items.length);
        all.push(...items.map((r) => ({ ...r, id: String(r.id) })));
        if (items.length < PAGE_SIZE || all.length >= count) break;
      }
      setRows(all);
      setTotal(count || all.length);
    } catch (e) {
      setLoadErr(errText(e));
    } finally {
      setLoading(false);
    }
  }, [def, baseUrl, token]);

  // form definition: the service's own schema when it has one, else the catalog
  useEffect(() => {
    setSchema(null);
    if (!def) return;
    let live = true;
    request(baseUrl, token, "GET", `${def.path}/schema`)
      .then((d) => live && setSchema(readSchema(d)))
      .catch(() => live && setSchema(null));
    return () => {
      live = false;
    };
  }, [def, baseUrl, token]);

  useEffect(() => {
    setQ("");
    setSort(null);
    setForm(null);
    setDel(null);
    setRows([]);
    reload();
  }, [reload]);

  const fields = useMemo<Fld[]>(() => {
    if (!def) return [];
    if (schema) return schema;
    const catalog = def.f.map(([n, t, r]) => toFld(n, t, !!r));
    const known = new Set(catalog.map((f) => f.name));
    const extra = sampleFields(rows, known);
    // nothing on screen yet to learn the base fields from: the service always carries a name
    const hasLabel = [...catalog, ...extra].some((f) => /^(name|title|label|code)$/i.test(f.name));
    const base = !hasLabel && rows.length === 0 ? [toFld("name", "string", true)] : [];
    return [...base, ...extra, ...catalog];
  }, [def, schema, rows]);

  // dropdown choices for fields that point at other records
  useEffect(() => {
    if (!def) return;
    let live = true;
    const wanted = fields.filter((f) => f.type === "guid" && LOOKUP[f.name]);
    wanted.forEach(async (f) => {
      const lk = LOOKUP[f.name];
      try {
        let list: Rec[];
        if (lk.key === "self") list = rows;
        else {
          const data = await request(baseUrl, token, "GET", `${pathOf(lk.key)}?page=1&pageSize=${PAGE_SIZE}`);
          list = Array.isArray(data) ? data : data?.items ?? [];
        }
        const opts = list.map((r) => ({ v: String(lk.by ? r[lk.by] ?? "" : r.id), l: labelOf(r) })).filter((o) => o.v);
        if (live) setOptions((p) => ({ ...p, [f.name]: opts }));
      } catch {
        /* leave it as a plain id box */
      }
    });
    return () => {
      live = false;
    };
  }, [def, fields, baseUrl, token, rows]);

  const cols = useMemo(() => pickColumns(rows, fields), [rows, fields]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let out = rows.filter((r) => !needle || cols.some((c) => String(r[c.key] ?? "").toLowerCase().includes(needle)));
    if (sort) {
      const dir = sort.dir;
      out = [...out].sort((a, b) => {
        const x = a[sort.key];
        const y = b[sort.key];
        return (typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""))) * dir;
      });
    }
    return out;
  }, [rows, q, sort, cols]);

  const activeCol = cols.find((c) => ACTIVE_KEYS.test(c.key));
  const activeCount = activeCol ? rows.filter((r) => r[activeCol.key]).length : null;

  const submit = async (values: Record<string, unknown>): Promise<string | null> => {
    if (!def) return null;
    const rec = form?.rec ?? null;
    if (rec && Object.keys(values).length === 0) {
      setForm(null);
      return null;
    }
    try {
      if (rec) {
        await request(baseUrl, token, "PATCH", `${def.path}/${rec.id}`, values, rec.version ? { "If-Match": `"${rec.version}"` } : {});
        notify(`${def.l} saved`);
      } else {
        await request(baseUrl, token, "POST", def.path, values);
        notify(`${def.l} created`);
      }
      setForm(null);
      await reload();
      return null;
    } catch (e) {
      const m = errText(e);
      if (/changed this record/.test(m)) await reload();
      return m;
    }
  };

  const confirmDelete = async () => {
    if (!del || !def) return;
    setBusy(true);
    try {
      await request(baseUrl, token, "DELETE", `${def.path}/${del.id}`);
      notify(`${labelOf(del)} deleted`);
      setDel(null);
      await reload();
    } catch (e) {
      notify(errText(e), "error");
      setDel(null);
    } finally {
      setBusy(false);
    }
  };

  if (!def) return <div className={styles.accWrap}>This setup screen isn't available.</div>;

  return (
    <div className={styles.accWrap}>
      <Toaster toasterId={toasterId} />

      <header className={styles.accHead}>
        <div>
          <h2 className={styles.accTitle}>{def.p}</h2>
          <p className={styles.accSub}>CRM setup. Changes apply to every CRM screen that uses {def.p.toLowerCase()}.</p>
        </div>
        <Button appearance="primary" icon={<Add20Regular />} onClick={() => setForm({ rec: null })} disabled={loading && rows.length === 0}>
          New {def.l.toLowerCase()}
        </Button>
      </header>

      {loadErr && (
        <div className={styles.accErr} role="alert">
          {loadErr}
          <Button size="small" appearance="subtle" onClick={reload}>
            Try again
          </Button>
        </div>
      )}

      <div className={styles.accTiles}>
        <div className={styles.accTile}>
          <span className={styles.accTileLabel}>{def.p}</span>
          <span className={styles.accTileValue}>{loading && rows.length === 0 ? "…" : total.toLocaleString("en-US")}</span>
          <span className={styles.accTileNote}>{activeCount !== null ? `${activeCount} active` : "In the CRM service"}</span>
        </div>
      </div>

      <section className={styles.accCard}>
        <div className={styles.accToolbar}>
          <div className={styles.accSearch}>
            <Search20Regular />
            <input className={styles.accSearchInput} placeholder={`Search ${def.p.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search" />
          </div>
          <span className={styles.accSpacer} />
          <span className={styles.accCount}>
            {shown.length} of {rows.length}
          </span>
          <Button appearance="subtle" icon={<ArrowSync20Regular />} onClick={reload} disabled={loading} aria-label="Refresh" title="Refresh" />
        </div>

        <div className={styles.accTableWrap}>
          <table className={styles.accTable}>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => setSort((p) => (p && p.key === c.key ? (p.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 }))}
                    aria-sort={sort?.key === c.key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
                  >
                    {c.label}
                    <span className={styles.accSortMark}>{sort?.key === c.key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</span>
                  </th>
                ))}
                <th className={styles.accRight} aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {loading &&
                rows.length === 0 &&
                [0, 1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td colSpan={Math.max(cols.length, 3) + 1}>
                      <span className={styles.accSk} style={{ width: `${60 + ((i * 11) % 30)}%`, height: 12 }} />
                    </td>
                  </tr>
                ))}
              {shown.map((r) => (
                <tr key={r.id}>
                  {cols.map((c) => (
                    <td key={c.key} className={c.key === "number" ? styles.accMono : undefined}>
                      {showCell(c.key, r[c.key])}
                    </td>
                  ))}
                  <td className={styles.accRight}>
                    <span className={styles.accActions}>
                      <button className={styles.accIconBtn} title="Edit" aria-label={`Edit ${labelOf(r)}`} onClick={() => setForm({ rec: r })}>
                        <Edit20Regular />
                      </button>
                      <button className={`${styles.accIconBtn} ${styles.accIconDanger}`} title="Delete" aria-label={`Delete ${labelOf(r)}`} onClick={() => setDel(r)}>
                        <Delete20Regular />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && shown.length === 0 && !loadErr && (
            <div className={styles.accEmpty}>
              {rows.length === 0 ? `No ${def.p.toLowerCase()} yet.` : "Nothing matches your search."}
              {rows.length === 0 && (
                <Button appearance="primary" size="small" icon={<Add20Regular />} onClick={() => setForm({ rec: null })}>
                  New {def.l.toLowerCase()}
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {form && (
        <RecordDialog
          key={form.rec?.id ?? "new"}
          title={form.rec ? `Edit ${def.l.toLowerCase()}` : `New ${def.l.toLowerCase()}`}
          submit={form.rec ? "Save changes" : "Create"}
          fields={fields}
          rec={form.rec}
          options={options}
          onClose={() => setForm(null)}
          onSave={submit}
        />
      )}

      {del && (
        <Dialog open onOpenChange={(_, d) => !d.open && setDel(null)}>
          <DialogSurface className={styles.accDialog}>
            <DialogBody>
              <DialogTitle>Delete {def.l.toLowerCase()}?</DialogTitle>
              <DialogContent>
                <b>{labelOf(del)}</b> will be removed. This can't be undone.
              </DialogContent>
              <DialogActions>
                <DialogTrigger action="close">
                  <Button>Cancel</Button>
                </DialogTrigger>
                <Button appearance="primary" className={styles.accDangerBtn} disabled={busy} onClick={confirmDelete}>
                  Delete
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

export default SettupCrm;